import { useEffect, useMemo, useState } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import { Check, Loader2, Mail, Phone, Send, UserMinus, UserPlus, Users, X } from 'lucide-react'
import toast from 'react-hot-toast'
import { useTranslation } from 'react-i18next'
import { contactApi } from '@/services/api'
import type { Contact, DiscoveredDjeloUser, FriendRequest } from '@/types'
import { Country } from '@/data/countries'
import { findCountryByPhoneCode } from '@/data/countries'
import { ContactHeader, AddContactModal, SendMoneyToContactModal } from '@/components/contact'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Textarea } from '@/components/ui/Textarea'

export function ContactsPage() {
  const { t } = useTranslation()
  const [searchTerm, setSearchTerm] = useState('')
  const [contacts, setContacts] = useState<Contact[]>([])
  const [discoveredUsers, setDiscoveredUsers] = useState<DiscoveredDjeloUser[]>([])
  const [incomingFriendRequests, setIncomingFriendRequests] = useState<FriendRequest[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [isDiscovering, setIsDiscovering] = useState(false)
  const [isLoadingFriendRequests, setIsLoadingFriendRequests] = useState(false)
  const [showAddModal, setShowAddModal] = useState(false)
  const [showFriendRequestsModal, setShowFriendRequestsModal] = useState(false)
  const [showRequestMoneyModal, setShowRequestMoneyModal] = useState(false)
  const [addMode, setAddMode] = useState<'phone' | 'manual'>('phone')
  const [deviceContacts, setDeviceContacts] = useState<any[]>([])
  const [selectedDeviceContact, setSelectedDeviceContact] = useState<any | null>(null)
  
  // Manual form state
  const [name, setName] = useState('')
  const [phoneNumber, setPhoneNumber] = useState('')
  const [selectedCountry, setSelectedCountry] = useState<Country | null>(null)
  const [email, setEmail] = useState('')
  const [isSaving, setIsSaving] = useState(false)
  const [selectedContactForMoney, setSelectedContactForMoney] = useState<Contact | null>(null)
  const [showSendMoneyModal, setShowSendMoneyModal] = useState(false)
  const [requestAmount, setRequestAmount] = useState('')
  const [requestDescription, setRequestDescription] = useState('')
  const [requestTarget, setRequestTarget] = useState<{
    name: string
    recipient_user_id?: string
    recipient_phone?: string
    recipient_email?: string
  } | null>(null)
  const [isSubmittingRequestMoney, setIsSubmittingRequestMoney] = useState(false)

  useEffect(() => {
    loadContacts()
    loadIncomingFriendRequests()
  }, [])

  const parseList = <T,>(payload: any): T[] => {
    const data = payload?.data ?? payload
    if (Array.isArray(data)) return data as T[]
    if (Array.isArray(data?.results)) return data.results as T[]
    if (Array.isArray(payload?.results)) return payload.results as T[]
    return []
  }

  const loadContacts = async () => {
    setIsLoading(true)
    try {
      const response = await contactApi.getContacts()
      setContacts(parseList<Contact>(response.data))
    } catch (error) {
      console.error('Error loading contacts:', error)
      setContacts([])
    } finally {
      setIsLoading(false)
    }
  }

  const loadIncomingFriendRequests = async () => {
    setIsLoadingFriendRequests(true)
    try {
      const response = await contactApi.getIncomingFriendRequests()
      setIncomingFriendRequests(parseList<FriendRequest>(response.data))
    } catch (error) {
      console.error('Error loading friend requests:', error)
      setIncomingFriendRequests([])
    } finally {
      setIsLoadingFriendRequests(false)
    }
  }

  const normalizePhone = (value?: string | null) => (value || '').replace(/\D/g, '')
  const normalizeEmail = (value?: string | null) => (value || '').trim().toLowerCase()

  const discoverUsersFromDeviceContacts = async (contactsToCheck: any[]) => {
    const payload = contactsToCheck
      .map((contact) => ({
        name: (contact.name?.[0] || '').toString(),
        phone: (contact.tel?.[0] || '').toString().trim(),
        email: (contact.email?.[0] || '').toString().trim(),
      }))
      .filter((entry) => entry.phone || entry.email)

    if (payload.length === 0) {
      setDiscoveredUsers([])
      return
    }

    setIsDiscovering(true)
    try {
      const response = await contactApi.discoverDjeloUsers(payload)
      setDiscoveredUsers(parseList<DiscoveredDjeloUser>(response.data))
    } catch (error) {
      console.error('Error discovering Djelo users:', error)
      setDiscoveredUsers([])
    } finally {
      setIsDiscovering(false)
    }
  }

  const handleRequestPhoneContacts = async () => {
    try {
      // Contact Picker API - Available in Chrome 80+ on Android
      // Check if the API is supported
      if ('contacts' in navigator && 'ContactsManager' in window) {
        const contactsManager = new (window as any).ContactsManager()
        
        try {
          // Request permission and let user pick contacts
          // The select() method opens the native contact picker
          // Available properties: 'name', 'tel', 'email', 'address', 'icon'
          const contacts = await contactsManager.select(['name', 'tel', 'email'], {
            multiple: true // Allow user to select multiple contacts
          })
          
          if (contacts && contacts.length > 0) {
            setDeviceContacts(contacts)
            void discoverUsersFromDeviceContacts(contacts)
            setAddMode('phone')
            setShowAddModal(true)
          } else {
            // User selected no contacts, show manual entry
            setAddMode('manual')
            setShowAddModal(true)
          }
        } catch (err: any) {
          if (err.name === 'NotAllowedError' || err.name === 'SecurityError') {
            // Permission denied - show manual entry directly without alert
            setAddMode('manual')
            setShowAddModal(true)
          } else if (err.name === 'NotFoundError' || err.name === 'AbortError') {
            // User cancelled or no contacts found
            setAddMode('manual')
            setShowAddModal(true)
          } else {
            console.error('Error accessing contacts:', err)
            // Fallback to manual entry
            setAddMode('manual')
            setShowAddModal(true)
          }
        }
      } else {
        // Contact Picker API not available
        // This API is only available in:
        // - Chrome 80+ on Android
        // - Requires HTTPS (or localhost for development)
        // - Only works on mobile devices, not desktop browsers
        // Just open manual entry modal directly (no alert)
        setAddMode('manual')
        setShowAddModal(true)
      }
    } catch (error) {
      console.error('Error requesting contacts:', error)
      // Fallback to manual entry
      setAddMode('manual')
      setShowAddModal(true)
    }
  }

  const handleSelectDeviceContact = async (deviceContact: any) => {
    setSelectedDeviceContact(deviceContact)
    
    // Extract phone number (get first one)
    const phone = deviceContact.tel?.[0] || ''
    const name = (deviceContact.name?.[0] || '').toString()
    const emailValue = (deviceContact.email?.[0] || '').toString()
    
    // Try to detect country code from phone
    let country: Country | null = null
    let phoneWithoutCode = phone
    
    if (phone) {
      // Remove all non-digit characters except +
      const cleanPhone = phone.replace(/[^\d+]/g, '')
      
      // Try to find country code
      for (let i = 1; i <= 4; i++) {
        const code = cleanPhone.substring(0, i)
        const found = findCountryByPhoneCode(code)
        if (found) {
          country = found
          phoneWithoutCode = cleanPhone.substring(i)
          break
        }
      }
      
      // Default to São Tomé if no country found
      if (!country) {
        country = findCountryByPhoneCode('+239') || null
        phoneWithoutCode = cleanPhone.replace(/^\+?239/, '')
      }
    } else {
      country = findCountryByPhoneCode('+239') || null
    }
    
    setName(name)
    setPhoneNumber(phoneWithoutCode)
    setSelectedCountry(country)
    setEmail(emailValue)
    setAddMode('manual')
  }

  const handlePhoneNumberChange = (fullNumber: string, country: Country) => {
    const numberWithoutCode = fullNumber.replace(country.phoneCode, '')
    setPhoneNumber(numberWithoutCode)
    setSelectedCountry(country)
  }

  const reloadContactsContext = async () => {
    await Promise.all([
      loadContacts(),
      loadIncomingFriendRequests(),
      discoverUsersFromDeviceContacts(deviceContacts),
    ])
  }

  const handleSaveContact = async () => {
    const trimmedName = (name || '').toString().trim()
    const trimmedPhone = (phoneNumber || '').toString().trim()
    const trimmedEmail = (email || '').toString().trim()
    
    if (!trimmedName) {
      alert(t('contacts.nameRequired', 'O nome é obrigatório'))
      return
    }
    
    if (!trimmedPhone && !trimmedEmail) {
      alert(t('contacts.phoneOrEmailRequired', 'É necessário preencher pelo menos telefone ou email'))
      return
    }
    
    if (trimmedPhone && !selectedCountry) {
      alert(t('contacts.countryRequired', 'É necessário selecionar o país para o telefone'))
      return
    }

    setIsSaving(true)
    try {
      const contactData: {
        name: string
        phone_number?: string
        email?: string
        country_code?: string
      } = {
        name: trimmedName
      }
      
      if (trimmedPhone) {
        contactData.phone_number = trimmedPhone
        contactData.country_code = selectedCountry?.phoneCode || '+239'
      }
      
      if (trimmedEmail) {
        contactData.email = trimmedEmail
      }
      
      await contactApi.createContact(contactData as Parameters<typeof contactApi.createContact>[0])
      
      // Reset form
      setName('')
      setPhoneNumber('')
      setEmail('')
      setSelectedCountry(null)
      setSelectedDeviceContact(null)
      setShowAddModal(false)
      
      // Reload contacts
      await reloadContactsContext()
    } catch (error: any) {
      console.error('Error saving contact:', error)
      const errorMsg = error.response?.data?.error || error.response?.data?.message || t('contacts.saveError', 'Erro ao guardar contacto')
      alert(errorMsg)
    } finally {
      setIsSaving(false)
    }
  }

  const handleModalClose = () => {
    setShowAddModal(false)
    setName('')
    setPhoneNumber('')
    setEmail('')
    setSelectedCountry(null)
    setSelectedDeviceContact(null)
  }

  const openManualAddFromDeviceContact = (deviceContact: any) => {
    void handleSelectDeviceContact(deviceContact)
    setShowAddModal(true)
  }

  const handleSendFriendRequest = async (user: DiscoveredDjeloUser) => {
    if (!user.user_id) return
    try {
      await contactApi.createFriendRequest(user.user_id)
      toast.success(t('contacts.friendRequestSent', 'Pedido de amizade enviado com sucesso'))
      await reloadContactsContext()
    } catch (error: any) {
      console.error('Error sending friend request:', error)
      toast.error(error?.response?.data?.error || t('contacts.saveError', 'Erro ao guardar contacto'))
    }
  }

  const handleAcceptFriendRequest = async (requestId?: string | null) => {
    if (!requestId) return
    try {
      await contactApi.acceptFriendRequest(requestId)
      toast.success(t('contacts.friendAdded', 'Amizade aceite com sucesso'))
      await reloadContactsContext()
    } catch (error: any) {
      console.error('Error accepting friend request:', error)
      toast.error(error?.response?.data?.error || t('common.error', 'Ocorreu um erro'))
    }
  }

  const handleRejectFriendRequest = async (requestId?: string | null) => {
    if (!requestId) return
    try {
      await contactApi.rejectFriendRequest(requestId)
      toast.success(t('contacts.friendRequestRejected', 'Pedido rejeitado'))
      await reloadContactsContext()
    } catch (error: any) {
      console.error('Error rejecting friend request:', error)
      toast.error(error?.response?.data?.error || t('common.error', 'Ocorreu um erro'))
    }
  }

  const handleRemoveFriend = async (contact: Contact) => {
    if (!window.confirm(t('contacts.confirmRemoveFriend', 'Remover este amigo?'))) return
    try {
      await contactApi.deleteContact(contact.id)
      toast.success(t('contacts.friendRemoved', 'Amizade removida com sucesso'))
      await reloadContactsContext()
    } catch (error: any) {
      console.error('Error removing friend:', error)
      toast.error(error?.response?.data?.error || t('common.error', 'Ocorreu um erro'))
    }
  }

  const openRequestMoneyModal = (target: {
    name: string
    recipient_user_id?: string
    recipient_phone?: string
    recipient_email?: string
  }) => {
    setRequestTarget(target)
    setRequestAmount('')
    setRequestDescription('')
    setShowRequestMoneyModal(true)
  }

  const handleSubmitRequestMoney = async () => {
    if (!requestTarget) return
    const amountValue = Number.parseFloat(requestAmount.replace(',', '.'))
    if (!Number.isFinite(amountValue) || amountValue <= 0) {
      toast.error(t('wallet.pleaseEnterAmount', 'Por favor, insira um valor'))
      return
    }

    setIsSubmittingRequestMoney(true)
    try {
      await contactApi.createMoneyRequest({
        recipient_user_id: requestTarget.recipient_user_id,
        recipient_phone: requestTarget.recipient_phone,
        recipient_email: requestTarget.recipient_email,
        amount: amountValue,
        description: requestDescription.trim(),
      })
      toast.success(t('contacts.requestMoneySent', 'Pedido de dinheiro enviado com sucesso'))
      setShowRequestMoneyModal(false)
      setRequestTarget(null)
      setRequestAmount('')
      setRequestDescription('')
    } catch (error: any) {
      console.error('Error creating money request:', error)
      toast.error(error?.response?.data?.error || t('common.error', 'Ocorreu um erro'))
    } finally {
      setIsSubmittingRequestMoney(false)
    }
  }

  const myContactIdentifiers = useMemo(() => {
    const ids = new Set<string>()
    contacts.forEach((contact) => {
      const phone = normalizePhone(`${contact.country_code || ''}${contact.phone_number || ''}`)
      const emailValue = normalizeEmail(contact.email)
      if (phone) ids.add(phone)
      if (emailValue) ids.add(`e:${emailValue}`)
    })
    return ids
  }, [contacts])

  const discoveredIdentifiers = useMemo(() => {
    const ids = new Set<string>()
    discoveredUsers.forEach((user) => {
      const matchedPhone = normalizePhone(user.matched_phone)
      const matchedEmail = normalizeEmail(user.matched_email)
      const phone = normalizePhone(user.phone_number)
      const emailValue = normalizeEmail(user.email)
      if (matchedPhone) ids.add(matchedPhone)
      if (matchedEmail) ids.add(`e:${matchedEmail}`)
      if (phone) ids.add(phone)
      if (emailValue) ids.add(`e:${emailValue}`)
    })
    return ids
  }, [discoveredUsers])

  const remainingDeviceContacts = useMemo(() => {
    return deviceContacts.filter((contact) => {
      const phone = normalizePhone((contact.tel?.[0] || '').toString())
      const emailValue = normalizeEmail((contact.email?.[0] || '').toString())
      if (phone && myContactIdentifiers.has(phone)) return false
      if (emailValue && myContactIdentifiers.has(`e:${emailValue}`)) return false
      if (phone && discoveredIdentifiers.has(phone)) return false
      if (emailValue && discoveredIdentifiers.has(`e:${emailValue}`)) return false
      return true
    })
  }, [deviceContacts, discoveredIdentifiers, myContactIdentifiers])

  const normalizedSearch = searchTerm.trim().toLowerCase()

  const filteredContacts = contacts.filter((contact) => {
    if (!normalizedSearch) return true
    return [
      contact.name,
      contact.phone_number,
      contact.email,
    ].some((value) => (value || '').toLowerCase().includes(normalizedSearch))
  })

  const filteredDiscoveredUsers = discoveredUsers.filter((user) => {
    if (!normalizedSearch) return true
    return [
      user.contact_name,
      user.name,
      user.matched_phone,
      user.matched_email,
      user.phone_number,
      user.email,
    ].some((value) => (value || '').toLowerCase().includes(normalizedSearch))
  })

  const filteredDeviceContacts = remainingDeviceContacts.filter((contact) => {
    if (!normalizedSearch) return true
    return [
      (contact.name?.[0] || '').toString(),
      (contact.tel?.[0] || '').toString(),
      (contact.email?.[0] || '').toString(),
    ].some((value) => value.toLowerCase().includes(normalizedSearch))
  })

  return (
    <div className="min-h-screen bg-background-light">
      <ContactHeader
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        onAddClick={handleRequestPhoneContacts}
        friendRequestCount={incomingFriendRequests.length}
        onFriendRequestsClick={() => setShowFriendRequestsModal(true)}
      />

      <div className="p-4 space-y-6">
        {isLoading ? (
          <div className="flex items-center justify-center py-12 text-text-secondary">
            <Loader2 className="mr-2 h-5 w-5 animate-spin" />
            {t('common.loading', 'A carregar...')}
          </div>
        ) : (
          <>
            {filteredContacts.length > 0 && (
              <section className="space-y-3">
                <SectionTitle title={t('contacts.djeloContacts', 'Os meus contactos Djelo')} />
                {filteredContacts.map((contact, index) => (
                  <PersonCard
                    key={contact.id}
                    index={index}
                    name={contact.name}
                    imageUrl={contact.profile_picture_url}
                    subtitle={contact.phone_number ? `${contact.country_code} ${contact.phone_number}` : contact.email}
                    secondarySubtitle={contact.phone_number && contact.email ? contact.email : undefined}
                    actions={
                      <>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setSelectedContactForMoney(contact)
                            setShowSendMoneyModal(true)
                          }}
                        >
                          <Send className="mr-1 h-4 w-4" />
                          {t('wallet.sendMoney', 'Enviar dinheiro')}
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() =>
                            openRequestMoneyModal({
                              name: contact.name,
                              recipient_phone: contact.phone_number
                                ? `${contact.country_code || ''}${contact.phone_number}`
                                : undefined,
                              recipient_email: contact.email,
                            })
                          }
                        >
                          {t('contacts.requestMoney', 'Pedir dinheiro')}
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRemoveFriend(contact)}
                        >
                          <UserMinus className="mr-1 h-4 w-4" />
                          {t('contacts.removeFriend', 'Remover')}
                        </Button>
                      </>
                    }
                  />
                ))}
              </section>
            )}

            {(isDiscovering || filteredDiscoveredUsers.length > 0) && (
              <section className="space-y-3">
                <SectionTitle title={t('contacts.djeloUsers', 'Utilizadores Djelo')} />
                {isDiscovering && (
                  <div className="flex items-center justify-center rounded-xl border border-gray-100 bg-white p-6 text-text-secondary">
                    <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                    {t('common.loading', 'A carregar...')}
                  </div>
                )}
                {filteredDiscoveredUsers.map((user, index) => {
                  const displayName = user.contact_name || user.name || user.phone_number || user.email || 'Djelo'
                  const subtitle = user.matched_phone || user.matched_email || user.phone_number || user.email || ''
                  return (
                    <PersonCard
                      key={user.user_id}
                      index={index}
                      name={displayName}
                      imageUrl={user.profile_picture_url}
                      subtitle={subtitle}
                      actions={
                        user.friend_request_direction === 'incoming' && user.friend_request_id ? (
                          <>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleRejectFriendRequest(user.friend_request_id)}
                            >
                              <X className="mr-1 h-4 w-4" />
                              {t('contacts.rejectFriendRequest', 'Rejeitar')}
                            </Button>
                            <Button
                              variant="primary"
                              size="sm"
                              onClick={() => handleAcceptFriendRequest(user.friend_request_id)}
                            >
                              <Check className="mr-1 h-4 w-4" />
                              {t('contacts.acceptFriendRequest', 'Aceitar')}
                            </Button>
                          </>
                        ) : user.friend_request_direction === 'outgoing' ? (
                          <span className="rounded-full bg-primary/10 px-3 py-2 text-xs font-semibold text-primary">
                            {t('contacts.friendRequestPending', 'Pedido pendente')}
                          </span>
                        ) : (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleSendFriendRequest(user)}
                          >
                            <UserPlus className="mr-1 h-4 w-4" />
                            {t('contacts.addFriend', 'Adicionar amigo')}
                          </Button>
                        )
                      }
                    />
                  )
                })}
              </section>
            )}

            {filteredDeviceContacts.length > 0 && (
              <section className="space-y-3">
                <SectionTitle title={t('contacts.phoneContacts', 'Contactos do telefone')} />
                {filteredDeviceContacts.map((contact, index) => {
                  const displayName = (contact.name?.[0] || '').toString() || 'Contacto'
                  const subtitle = (contact.tel?.[0] || contact.email?.[0] || '').toString()
                  return (
                    <PersonCard
                      key={`${displayName}-${subtitle}-${index}`}
                      index={index}
                      name={displayName}
                      subtitle={subtitle}
                      actions={
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => openManualAddFromDeviceContact(contact)}
                        >
                          <UserPlus className="mr-1 h-4 w-4" />
                          {t('contacts.addContact', 'Adicionar contacto')}
                        </Button>
                      }
                    />
                  )
                })}
              </section>
            )}

            {!isDiscovering &&
              filteredContacts.length === 0 &&
              filteredDiscoveredUsers.length === 0 &&
              filteredDeviceContacts.length === 0 && (
                <div className="rounded-2xl border border-dashed border-gray-200 bg-white px-6 py-12 text-center">
                  <Users className="mx-auto mb-4 h-12 w-12 text-gray-300" />
                  <p className="text-text-secondary">
                    {searchTerm
                      ? t('contacts.noResults', 'Nenhum resultado encontrado')
                      : t('contacts.noContacts', 'Ainda não tens contactos para mostrar')}
                  </p>
                </div>
              )}
          </>
        )}
      </div>

      <AddContactModal
        isOpen={showAddModal}
        mode={addMode}
        deviceContacts={deviceContacts}
        name={name}
        phoneNumber={phoneNumber}
        email={email}
        selectedCountry={selectedCountry}
        isSaving={isSaving}
        onClose={handleModalClose}
        onSelectDeviceContact={handleSelectDeviceContact}
        onNameChange={setName}
        onPhoneChange={handlePhoneNumberChange}
        onEmailChange={setEmail}
        onSave={handleSaveContact}
      />

      <SendMoneyToContactModal
        isOpen={showSendMoneyModal}
        contact={selectedContactForMoney}
        onClose={() => {
          setShowSendMoneyModal(false)
          setSelectedContactForMoney(null)
        }}
        onSuccess={() => {
          // Reload contacts if needed
          loadContacts()
        }}
      />

      <FriendRequestsModal
        isOpen={showFriendRequestsModal}
        isLoading={isLoadingFriendRequests}
        requests={incomingFriendRequests}
        onClose={() => setShowFriendRequestsModal(false)}
        onAccept={handleAcceptFriendRequest}
        onReject={handleRejectFriendRequest}
        t={t}
      />

      <RequestMoneyModal
        isOpen={showRequestMoneyModal}
        targetName={requestTarget?.name || ''}
        amount={requestAmount}
        description={requestDescription}
        isSubmitting={isSubmittingRequestMoney}
        onAmountChange={setRequestAmount}
        onDescriptionChange={setRequestDescription}
        onClose={() => {
          setShowRequestMoneyModal(false)
          setRequestTarget(null)
        }}
        onSubmit={handleSubmitRequestMoney}
        t={t}
      />
    </div>
  )
}

function SectionTitle({ title }: { title: string }) {
  return <h2 className="text-sm font-semibold uppercase tracking-wide text-text-secondary">{title}</h2>
}

function PersonCard({
  name,
  subtitle,
  secondarySubtitle,
  imageUrl,
  actions,
  index,
}: {
  name: string
  subtitle?: string
  secondarySubtitle?: string
  imageUrl?: string
  actions?: React.ReactNode
  index: number
}) {
  const initial = name.trim().charAt(0).toUpperCase() || '?'
  const hasImage = Boolean(imageUrl)

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: Math.min(index * 0.04, 0.2) }}
      className="rounded-xl border border-gray-100 bg-white p-4 shadow-sm"
    >
      <div className="flex items-start gap-3">
        <div
          className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-primary/10 text-lg font-bold text-primary"
          style={hasImage ? { backgroundImage: `url(${imageUrl})`, backgroundSize: 'cover', backgroundPosition: 'center' } : undefined}
        >
          {!hasImage && initial}
        </div>
        <div className="min-w-0 flex-1">
          <h3 className="truncate font-semibold text-text-primary">{name}</h3>
          {subtitle && (
            <div className="mt-1 flex items-center gap-2 text-sm text-text-secondary">
              {subtitle.includes('@') ? <Mail className="h-3.5 w-3.5 shrink-0" /> : <Phone className="h-3.5 w-3.5 shrink-0" />}
              <span className="truncate">{subtitle}</span>
            </div>
          )}
          {secondarySubtitle && (
            <div className="mt-1 flex items-center gap-2 text-sm text-text-secondary">
              {secondarySubtitle.includes('@') ? <Mail className="h-3.5 w-3.5 shrink-0" /> : <Phone className="h-3.5 w-3.5 shrink-0" />}
              <span className="truncate">{secondarySubtitle}</span>
            </div>
          )}
          {actions && <div className="mt-4 flex flex-wrap gap-2">{actions}</div>}
        </div>
      </div>
    </motion.div>
  )
}

function FriendRequestsModal({
  isOpen,
  isLoading,
  requests,
  onClose,
  onAccept,
  onReject,
  t,
}: {
  isOpen: boolean
  isLoading: boolean
  requests: FriendRequest[]
  onClose: () => void
  onAccept: (requestId?: string | null) => void
  onReject: (requestId?: string | null) => void
  t: any
}) {
  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 z-[110] bg-black/50"
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.96, y: 12 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.96, y: 12 }}
            className="fixed inset-0 z-[111] flex items-center justify-center p-4"
          >
            <div className="max-h-[80vh] w-full max-w-lg overflow-y-auto rounded-2xl bg-white shadow-xl">
              <div className="sticky top-0 flex items-center justify-between border-b border-gray-100 bg-white px-6 py-4">
                <h2 className="text-xl font-bold text-text-primary">
                  {t('contacts.friendRequests', 'Pedidos de amizade')}
                </h2>
                <button onClick={onClose} className="rounded-full p-2 transition-colors hover:bg-gray-100">
                  <X className="h-5 w-5 text-text-secondary" />
                </button>
              </div>
              <div className="p-6">
                {isLoading ? (
                  <div className="flex items-center justify-center py-8 text-text-secondary">
                    <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                    {t('common.loading', 'A carregar...')}
                  </div>
                ) : requests.length === 0 ? (
                  <div className="py-8 text-center text-text-secondary">
                    {t('contacts.noFriendRequests', 'Sem novos pedidos de amizade')}
                  </div>
                ) : (
                  <div className="space-y-3">
                    {requests.map((request) => (
                      <PersonCard
                        key={request.id}
                        index={0}
                        name={request.sender_name || 'Djelo'}
                        imageUrl={request.sender_profile_picture_url}
                        actions={
                          <>
                            <Button variant="outline" size="sm" onClick={() => onReject(request.id)}>
                              <X className="mr-1 h-4 w-4" />
                              {t('contacts.rejectFriendRequest', 'Rejeitar')}
                            </Button>
                            <Button variant="primary" size="sm" onClick={() => onAccept(request.id)}>
                              <Check className="mr-1 h-4 w-4" />
                              {t('contacts.acceptFriendRequest', 'Aceitar')}
                            </Button>
                          </>
                        }
                      />
                    ))}
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  )
}

function RequestMoneyModal({
  isOpen,
  targetName,
  amount,
  description,
  isSubmitting,
  onAmountChange,
  onDescriptionChange,
  onClose,
  onSubmit,
  t,
}: {
  isOpen: boolean
  targetName: string
  amount: string
  description: string
  isSubmitting: boolean
  onAmountChange: (value: string) => void
  onDescriptionChange: (value: string) => void
  onClose: () => void
  onSubmit: () => void
  t: any
}) {
  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 z-[110] bg-black/50"
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.96, y: 12 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.96, y: 12 }}
            className="fixed inset-0 z-[111] flex items-center justify-center p-4"
          >
            <div className="w-full max-w-md rounded-2xl bg-white shadow-xl">
              <div className="flex items-center justify-between border-b border-gray-100 px-6 py-4">
                <div>
                  <h2 className="text-xl font-bold text-text-primary">
                    {t('contacts.requestMoney', 'Pedir dinheiro')}
                  </h2>
                  <p className="text-sm text-text-secondary">{targetName}</p>
                </div>
                <button onClick={onClose} className="rounded-full p-2 transition-colors hover:bg-gray-100">
                  <X className="h-5 w-5 text-text-secondary" />
                </button>
              </div>
              <div className="space-y-4 p-6">
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  placeholder="0.00"
                  value={amount}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => onAmountChange(event.target.value)}
                />
                <Textarea
                  placeholder={t('contacts.requestDescription', 'Descrição')}
                  value={description}
                  onChange={(event) => onDescriptionChange(event.target.value)}
                />
                <div className="flex justify-end gap-2">
                  <Button variant="ghost" onClick={onClose} disabled={isSubmitting}>
                    {t('common.cancel', 'Cancelar')}
                  </Button>
                  <Button variant="primary" onClick={onSubmit} loading={isSubmitting}>
                    {t('contacts.requestMoneySend', 'Enviar pedido')}
                  </Button>
                </div>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  )
}

